import { useTranslation } from 'react-i18next';
import type { ReactNode } from 'react';

import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '@/components/ui/alert-dialog';
import { Button } from '@/components/ui/button';

interface ConfirmPlanChangeDialogProps {
  type: 'upgrade' | 'downgrade';
  planName: string;
  onConfirm: () => void;
  isLoading?: boolean;
  disabled?: boolean;
  children: ReactNode;
}

export function ConfirmPlanChangeDialog({
  type,
  planName,
  onConfirm,
  isLoading = false,
  disabled = false,
  children,
}: ConfirmPlanChangeDialogProps) {
  const { t } = useTranslation();

  return (
    <AlertDialog>
      <AlertDialogTrigger asChild disabled={disabled}>
        {children}
      </AlertDialogTrigger>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>
            {t(
              type === 'upgrade'
                ? 'customer.planManagement.dialogs.upgradeTitle'
                : 'customer.planManagement.dialogs.downgradeTitle',
              { plan: planName },
            )}
          </AlertDialogTitle>
          <AlertDialogDescription>
            {t(
              type === 'upgrade'
                ? 'customer.planManagement.dialogs.upgradeDescription'
                : 'customer.planManagement.dialogs.downgradeDescription',
            )}
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel>{t('customer.planManagement.actions.keepCurrent')}</AlertDialogCancel>
          <AlertDialogAction asChild>
            <Button onClick={onConfirm} disabled={isLoading}>
              {isLoading
                ? t('customer.planManagement.actions.processing')
                : t(
                    type === 'upgrade'
                      ? 'customer.planManagement.actions.confirmUpgrade'
                      : 'customer.planManagement.actions.confirmDowngrade',
                  )}
            </Button>
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
